import type { BrandStats } from '@ekorank/shared'

interface Props {
  stats: BrandStats
  modelsConfigured: number
  modelsCovering: number
  lastCheckedAt: number | null
}

export function KeyStatsCards({ stats, modelsConfigured, modelsCovering, lastCheckedAt }: Props) {
  const lastCheckText = lastCheckedAt ? formatRelative(lastCheckedAt) : 'Never'
  const avgRank = stats.current.avgRankWhenMentioned
  const avgRankText = avgRank != null ? `#${avgRank.toFixed(1)}` : '—'

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-10">
      <Card label="Mention Rate" value={`${stats.current.mentionRate}%`}
            delta={stats.deltas?.mentionRatePct ?? null} deltaUnit="pts" higherIsBetter />
      <Card label="Avg Rank When Mentioned" value={avgRankText}
            delta={stats.deltas?.avgRankDelta ?? null} deltaUnit="" higherIsBetter={false} />
      <Card label="Models Covering You"
            value={`${modelsCovering}/${modelsConfigured}`} />
      <Card label="Last Check" value={lastCheckText} />
    </div>
  )
}

function Card({
  label, value, delta = null, deltaUnit = '', higherIsBetter = true,
}: {
  label: string
  value: string | number
  delta?: number | null
  deltaUnit?: string
  higherIsBetter?: boolean
}) {
  const showDelta = delta != null && delta !== 0
  const isGood = showDelta && (higherIsBetter ? delta! > 0 : delta! < 0)
  const arrow = showDelta ? (delta! > 0 ? '↑' : '↓') : ''
  const deltaColor = !showDelta ? '' : isGood ? 'text-secondary' : 'text-error'

  return (
    <div className="bg-surface-container rounded-xl border border-outline-variant/10 p-4 sm:p-6">
      <p className="text-[10px] sm:text-xs font-mono text-outline uppercase tracking-widest mb-1.5 sm:mb-2">{label}</p>
      <div className="flex items-baseline gap-2 flex-wrap">
        <p className="text-2xl sm:text-3xl font-bold text-white">{value}</p>
        {showDelta && (
          <span className={`text-xs font-mono ${deltaColor}`}>
            {arrow}{Math.abs(delta!).toFixed(deltaUnit === '' ? 1 : 0)}{deltaUnit ? ` ${deltaUnit}` : ''}
          </span>
        )}
      </div>
    </div>
  )
}

function formatRelative(unixSec: number): string {
  const diff = Date.now() - unixSec * 1000
  const mins = Math.floor(diff / 60000)
  const hours = Math.floor(mins / 60)
  const days = Math.floor(hours / 24)
  if (days > 0) return `${days}d ago`
  if (hours > 0) return `${hours}h ago`
  if (mins > 0) return `${mins}m ago`
  return 'Just now'
}
